import {
	ErrorModal,
	useContextMenuMESF,
	useUserContext,
} from "@dexteel/mesf-core";
import { Grid2, Paper, Typography } from "@mui/material";
import {
	CellKeyDownEvent,
	ColDef,
	DefaultMenuItem,
	GetContextMenuItemsParams,
	GridApi,
	IStatusPanelParams,
	MenuItemDef,
	RowSelectedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useState } from "react";

import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { CenteredLazyLoading } from "@/controls/LazyLoading";

import { CustomStatusPanel } from "@/controls/ag-grid/components/CustomStatusPanel";
import { ExcelButton } from "@/controls/buttons/ExcelButton";
import { CustomSeparator } from "@/pages/catalogs/shared/components/CustomSeparator";
import { useResizablePanels } from "@/pages/catalogs/shared/hooks/useResizablePanels";
import { DeleteEntity } from "@/shared/components/DeleteEntity";
import { TextFieldSearchFilter } from "@/shared/components/TextFieldSearchFilter";
import { Group, Panel } from "react-resizable-panels";
import { useNavigate } from "react-router-dom";
import { Section } from "../../../models/Section";
import { deleteSection } from "../../../repositories/SectionRepository";
import { useSectionOptionsFunctions } from "../hooks/useSectionOptionsFunctions";
import { useTableData } from "../hooks/useTableData";
import { SectionDetails } from "./SectionDetails";
import { useSections } from "@/shared/selectors/SectionsOfPartSelector";

export const TableSection = ({
	sectionId,
}: {
	sectionId: string | undefined;
}) => {
	const navigate = useNavigate();
	const {
		state: { defaultAreaId },
	} = useUserContext();

	const [filterValue, setFilterValue] = useState("");
	const [selectedRowData, setSelectedRowData] = useState<Section | null>(null);
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [showModal, setShowModal] = useState<
		"create" | "update" | "delete" | ""
	>("");
	const [error, setError] = useState("");

	const {
		groupRef,
		getSavedPanelLayout,
		handlePanelResize,
		handleSeparatorDoubleClick,
	} = useResizablePanels("section-table-panel-sizes");

	const { data, isLoading } = useSections({
		onError: (error) => setError(error.message),
	});

	const rows = data?.sections;

	const handleNewSection = () => {
		if (gridApi) gridApi.deselectAll();
		setShowModal("create");
		setSelectedRowData(null);
		navigate(`/configuration/sections/new`);
	};

	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const { getMenuOptions } = useSectionOptionsFunctions({
		setSelectedRowData,
		setShowModal,
		handleNewSection,
	});
	const { columnDefs } = useTableData({
		setSelectedRowData,
		setShowModal,
		showContextMenu,
		setError,
	});

	const { defaultColDef } = useGridDefinitions({
		OnEdit: (data: any) => {
			setShowModal("update");
			setSelectedRowData(data);
		},
	});

	const onRowSelected = (event: RowSelectedEvent) => {
		const cell = gridApi?.getFocusedCell();
		if (cell?.column.getColId() !== "-" && event.node.isSelected()) {
			setSelectedRowData(event.data);
			setShowModal("update");
			navigate(`/configuration/sections/${(event.data as Section).SectionId}`);
		}
	};

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const data = params.node?.data;
			showContextMenu(event as any, data, "sectionContext");
			if (data) {
				params.api.deselectAll();
				params.node?.setSelected(true);
			}
			return [];
		},
		[rows],
	);

	const onCellKeyDown = (event: CellKeyDownEvent) => {
		const { api, node, event: keyEvent } = event;
		if (!keyEvent) return;
		const key = (keyEvent as KeyboardEvent).key;

		if (key === "ArrowUp" || key === "ArrowDown") {
			keyEvent.preventDefault();
			const currentIndex = node.rowIndex !== null ? node.rowIndex : -1;
			const nextRowIndex =
				key === "ArrowUp" ? currentIndex - 1 : currentIndex + 1;
			const nextNode = api.getDisplayedRowAtIndex(nextRowIndex);

			if (nextNode) {
				nextNode.setSelected(true);
				api.ensureIndexVisible(nextRowIndex);
				setSelectedRowData(nextNode.data);
				navigate(
					`/configuration/sections/${(nextNode.data as Section).SectionId}`,
				);
			}
		}
	};

	const onModalHide = ({
		shouldUpdate,
		close,
		entityId,
	}: {
		shouldUpdate: boolean;
		close: boolean;
		entityId?: number;
	}) => {
		if (close) {
			setShowModal("");
			setSelectedRowData(null);
			if (gridApi) gridApi?.deselectAll();
			return;
		}
		if (entityId && shouldUpdate) {
			setTimeout(() => {
				const rowNode = gridApi?.getRowNode(String(entityId));

				if (rowNode!) {
					gridApi?.ensureNodeVisible(rowNode);
					rowNode.setSelected(true);
					setSelectedRowData(rowNode.data);
				}
			}, 0);
		}
	};

	const handleExportToExcel = () => {
		if (gridApi) {
			const now = new Date();
			const year = now.getFullYear();
			const month = String(now.getMonth() + 1).padStart(2, "0");
			const day = String(now.getDate()).padStart(2, "0");
			const formattedDate = `${year}-${month}-${day}`;

			const columnKeys = gridApi
				.getColumnDefs()
				?.filter(
					(col): col is ColDef =>
						"field" in col && typeof col.field === "string",
				)
				.filter((col) => col.field !== "-")
				.map((col) => col.field)
				.filter((field): field is string => typeof field === "string");

			const params = {
				skipHeader: false,
				skipFooters: true,
				skipGroups: true,
				fileName: `sections_${formattedDate}.xlsx`,
				columnKeys: columnKeys,
				sheetName: "Sections",
				allColumns: false,
			};

			gridApi.exportDataAsExcel(params);
		} else {
			setError("Unable to export data. Please try again.");
		}
	};

	useEffect(() => {
		if (!isLoading) {
			if (sectionId) {
				if (sectionId === "new") {
					handleNewSection();
				} else {
					const sectionNode = gridApi?.getRowNode(sectionId);

					if (sectionNode) {
						gridApi?.ensureNodeVisible(sectionNode);

						sectionNode.setSelected(true);
						setSelectedRowData(sectionNode.data);
						setShowModal("update");
					}
				}
			}
		}
	}, [isLoading, gridApi]);

	useEffect(() => {
		registerConfig({
			id: "sectionContext",
			getOptions: getMenuOptions,
		});
	}, []);

	return (
		<>
			<Grid2 container justifyContent="flex-start" p={1} spacing={1}>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Typography variant="h5" fontWeight={600}>
						Sections
					</Typography>
				</Grid2>
				<Grid2 container size={{ md: 12 }} spacing={1} alignItems="center">
					<Grid2 size={{ xs: 12, md: 10 }}>
						<TextFieldSearchFilter
							value={filterValue}
							onChange={(val) => {
								setFilterValue(val);
								gridApi?.setGridOption("quickFilterText", val);
							}}
						/>
					</Grid2>
					<Grid2
						size={{ xs: 12, md: 2 }}
						justifyContent="flex-end"
						display="flex"
					>
						<ExcelButton
							handleExportToExcel={handleExportToExcel}
							disabled={!rows || rows.length === 0}
						/>
					</Grid2>
				</Grid2>
				<Grid2 sx={{ width: "100%", height: "70vh" }}>
					<Group
						orientation="horizontal"
						defaultLayout={getSavedPanelLayout()}
						onLayoutChange={handlePanelResize}
						groupRef={groupRef}
						style={{ height: "100%" }}
					>
						<Panel id="table" minSize={20}>
							<Paper elevation={2} style={{ height: "100%", width: "100%" }}>
								<AgGridReact<Section>
									loading={isLoading}
									rowData={
										rows?.sort(
											(a, b) => (a.SortOrder ?? 0) - (b.SortOrder ?? 0),
										) || []
									}
									columnDefs={columnDefs}
									defaultColDef={defaultColDef}
									rowHeight={34}
									headerHeight={34}
									animateRows
									getContextMenuItems={getContextMenuItems}
									onGridReady={(params) => setGridApi(params.api)}
									getRowId={(params) =>
										String((params.data as Section).SectionId)
									}
									loadingOverlayComponent={CenteredLazyLoading}
									onRowSelected={onRowSelected}
									onCellKeyDown={onCellKeyDown}
									gridOptions={{
										rowSelection: {
											mode: "singleRow",
											checkboxes: false,
											enableClickSelection: true,
										},
										statusBar: {
											statusPanels: [
												{
													statusPanel: "agTotalRowCountComponent",
													align: "right",
												},
												{
													statusPanel: (params: IStatusPanelParams) => (
														<CustomStatusPanel handleNew={handleNewSection} />
													),
													align: "left",
												},
											],
										},
									}}
								/>
							</Paper>
						</Panel>
						<CustomSeparator onDoubleClick={handleSeparatorDoubleClick} />
						<Panel id="details" minSize={20}>
							<Paper elevation={2} style={{ height: "100%", width: "100%" }}>
								<SectionDetails
									section={selectedRowData}
									onHide={onModalHide}
									modal={showModal}
								/>
							</Paper>
						</Panel>
					</Group>
				</Grid2>
			</Grid2>
			<DeleteEntity
				show={showModal === "delete"}
				entityParams={{
					Id: selectedRowData?.SectionId!,
					Name: selectedRowData?.SectionName!,
					Entity: "section",
				}}
				onHide={(update: boolean) => {
					onModalHide({ shouldUpdate: update, close: true });
					setShowModal(update ? "" : "update");
				}}
				deleteFunction={(sectionId: string) =>
					deleteSection({ sectionId: Number(sectionId), defaultAreaId })
				}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
